import type { CustomerExpense } from '@clockwise/clients/customer-expenses';
import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import { ApiError, expensePeriodLabel } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RowActions } from '@/components/custom/row-actions';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageWindow } from '@/hooks/use-page-window';
import { customerExpensesApi, expenseCatalogsApi } from '@/lib/api';
import { isForbidden } from '@/lib/errors';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso } from '@/lib/utils';
import { AsOfFilterToggle } from './as-of-filter-toggle';
import { ConfirmDialog } from './confirm-dialog';
import { CustomerExpenseFormSheet } from './customer-expense-form-sheet';
import { formatCostOrVariable } from './expense-catalog-form';
import { PaginationFooter } from './pagination-footer';
import { RequirePermission } from './require-permission';

/** Server page size. A long-lived customer accumulates a line per item per
 *  repricing, so this is paged rather than fetched whole (G13). */
const PAGE_SIZE = 25;

interface Props {
  customerId: string;
}

type SheetState = { mode: 'add' } | { mode: 'edit'; expense: CustomerExpense } | null;

/**
 * Customer-detail Invoicing tab → Pass-through pane.
 *
 * One row per line the customer is billed for. The lines are a TEMPLATE, meant
 * to seed each newly generated invoice so the invoice keeps its own copy.
 *
 * Deliberately NOT stated in the UI yet: nothing generates invoices from these
 * rows today, so copy promising "applied to invoices generated after today,
 * existing drafts unchanged" asserted a pipeline — and a draft-invoice concept
 * — that does not exist. Say it when generation lands and the claim is true.
 *
 * There is no on/off switch. "This customer has billed expenses" is simply
 * whether any line exists; a toggle beside the rows would be a second source of
 * truth that could contradict them.
 */
export function CustomerExpensesPanel({ customerId }: Props) {
  const queryClient = useQueryClient();
  const canEdit = useHasPermission('customer.expense.edit');

  const [asOf, setAsOf] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [confirmDelete, setConfirmDelete] = useState<CustomerExpense | null>(null);

  useEffect(() => {
    setPage(0);
  }, [asOf, showAll]);

  const offset = page * PAGE_SIZE;
  // `showAll` drops the as-of filter entirely; otherwise snapshot on the
  // chosen date, defaulting to today.
  const effectiveAsOf = showAll ? undefined : asOf || localTodayIso();

  const query = useQuery({
    queryKey: queryKeys.customerExpenses.list(customerId, {
      asOf: effectiveAsOf,
      limit: PAGE_SIZE,
      offset,
    }),
    queryFn: () =>
      customerExpensesApi.list(customerId, {
        asOf: effectiveAsOf,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const expenses = query.data?.data ?? [];

  // Catalog names + invoice periods for the rows on this page. The line stores
  // its own description and cost but NOT the item's name or cadence, so those
  // are resolved here. Fetched by exact id (never capped), so a retired item
  // still resolves — a line can outlive its catalog entry's active flag.
  const catalogIds = Array.from(new Set(expenses.map((e) => e.expenseCatalogId))).sort();
  const catalogQuery = useQuery({
    queryKey: [...queryKeys.expenseCatalogs.all, 'byIds', catalogIds] as const,
    queryFn: () => expenseCatalogsApi.findByIds(catalogIds),
    enabled: catalogIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const catalogById = new Map(
    (catalogQuery.data?.data ?? []).map((c: ExpenseCatalog) => [c.id, c]),
  );

  /**
   * Catalog display for one row. Three distinct states, because collapsing them
   * lies to the reader (G15):
   *   - in flight        → '…'
   *   - lookup failed    → the line's own description, plus a banner above;
   *                        "Unknown item" here would claim the item was deleted
   *                        when in fact the lookup never arrived
   *   - settled, missing → 'Unknown expense item', a genuine data gap
   */
  const catalogDisplay = (id: string): { name: string; period: string } => {
    const item = catalogById.get(id);
    if (item) return { name: item.name, period: expensePeriodLabel(item.period) };
    if (catalogQuery.isPending) return { name: '…', period: '…' };
    if (catalogQuery.isError) return { name: '—', period: '—' };
    return { name: 'Unknown expense item', period: '—' };
  };

  const total = query.data?.meta?.total ?? 0;
  const { pageCount, shownOffset } = usePageWindow({
    page,
    setPage,
    pageSize: PAGE_SIZE,
    total,
    responseOffset: query.data?.meta?.offset,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.customerExpenses.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerExpensesApi.delete(id),
    onSuccess: () => {
      toast.success('Expense deleted');
      invalidate();
      setConfirmDelete(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete'),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <AsOfFilterToggle
          asOf={asOf}
          onAsOfChange={setAsOf}
          showAll={showAll}
          onShowAllChange={setShowAll}
          showAllLabel="Show ended expenses"
          statusText={
            showAll
              ? 'Showing every expense, past and present.'
              : asOf
                ? `Showing what was billed on ${asOf}.`
                : 'Showing what is billed today.'
          }
        />
        <div className="ml-auto flex items-center gap-2">
          <RequirePermission permission="customer.expense.edit">
            {/* ONE add path, and it is the same sheet Edit opens. Adding used
                to be split in two — a multi-select catalog picker that took
                every item's catalog price, plus a single-line form for anything
                needing its own amount — which made "add an expense for $275"
                a two-step job: pick it, then find the row and edit it.
                Since the same catalog item may legitimately be billed more
                than once (one line per subsidiary), selecting an item was
                never a choice that could be made once and reused; there was
                nothing for a separate picking step to settle. So the form
                collects the item AND its description and amount together, and
                a line looks the same going in as it does coming back out. */}
            <Button size="sm" onClick={() => setSheet({ mode: 'add' })}>
              <Plus />
              Add expense
            </Button>
          </RequirePermission>
        </div>
      </div>

      {/* Lines loaded but their catalog NAMES didn't. The descriptions and
          amounts below are still correct and worth showing, so this is a
          banner rather than an error state that hides them. */}
      {catalogQuery.isError ? (
        <p className="rounded-md border border-destructive/50 p-2 text-xs text-destructive">
          Couldn’t load expense-item names, so the Expense column below shows each line’s own
          description only.
        </p>
      ) : null}

      <div className="rounded-md border">
        {query.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : query.isError ? (
          isForbidden(query.error) ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              You don’t have access to this customer’s expenses.
            </p>
          ) : (
            <p className="p-4 text-sm text-destructive">
              {query.error instanceof ApiError ? query.error.message : 'Failed to load expenses.'}
            </p>
          )
        ) : expenses.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {showAll
              ? 'No expenses on record. This customer is not billed for any catalog items.'
              : 'Nothing billed on this date. Ended or future expenses may exist — tick “Show ended expenses”.'}
          </p>
        ) : (
          <Table className="table-fixed">
            {/* `bg-surface-subtle` — the same cream header tint every other
                list table uses (see `sticky-table-scroll.tsx`); this table
                isn't wrapped in that component (it scrolls with the page,
                not internally), so the tint has to be stated here instead. */}
            <TableHeader className="bg-surface-subtle">
              <TableRow>
                <TableHead>Expense</TableHead>
                <TableHead className="w-32">Invoice period</TableHead>
                <TableHead className="w-28 text-right">Cost</TableHead>
                <TableHead className="w-48">Effective</TableHead>
                <TableHead className="w-24 text-right" aria-label="actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((e) => {
                const item = catalogDisplay(e.expenseCatalogId);
                const ended = e.effectiveTo != null && e.effectiveTo <= localTodayIso();
                return (
                  <TableRow key={e.id}>
                    <TableCell className="min-w-0">
                      <div className="truncate font-medium" title={item.name}>
                        {item.name}
                      </div>
                      {/* The line's OWN description — what actually lands on
                          the invoice, and the only thing distinguishing two
                          lines from the same catalog item. */}
                      <div className="truncate text-xs text-muted-foreground" title={e.description}>
                        {e.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{item.period}</TableCell>
                    {/* `formatCost` returns the raw string rather than a bogus
                        $0.00 when the stored value isn't a finite number, so a
                        data problem can't render as a real charge (G15). */}
                    {/* A line whose item is variable has no amount, and says
                        so rather than showing $0.00 — which would read as a
                        real, agreed price of nothing. `num` (tabular figures)
                        is dropped for that case; the word is not a number. */}
                    <TableCell
                      className={
                        e.cost === null
                          ? 'text-right text-sm text-muted-foreground'
                          : 'text-right text-sm tabular-nums'
                      }
                    >
                      {formatCostOrVariable(e.cost)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {e.effectiveFrom} →{' '}
                      <span className={ended ? 'text-muted-foreground' : 'font-medium'}>
                        {e.effectiveTo ?? 'Ongoing'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit ? (
                        <RowActions>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            // Two lines from the same catalog item can carry
                            // the SAME description — the add form prefills the
                            // catalog's and nothing forces it to be changed —
                            // so the description alone does not name a row
                            // uniquely. `effectiveFrom` is what distinguishes
                            // them on screen, so it belongs in the accessible
                            // name too, or a screen reader announces two
                            // identical Edit buttons.
                            aria-label={`Edit ${e.description} (from ${e.effectiveFrom})`}
                            onClick={() => setSheet({ mode: 'edit', expense: e })}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Delete ${e.description} (from ${e.effectiveFrom})`}
                            onClick={() => setConfirmDelete(e)}
                          >
                            <Trash2 />
                          </Button>
                        </RowActions>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <PaginationFooter
        shownOffset={shownOffset}
        shownCount={expenses.length}
        total={total}
        pageSize={PAGE_SIZE}
        page={page}
        pageCount={pageCount}
        isFetching={query.isFetching}
        onPrevious={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      <CustomerExpenseFormSheet
        open={sheet != null}
        onOpenChange={(o) => {
          if (!o) setSheet(null);
        }}
        customerId={customerId}
        // null puts the sheet in add mode; the sheet keys its own reset on
        // this, so the two modes never share stale field values.
        expense={sheet?.mode === 'edit' ? sheet.expense : null}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        onCancel={() => setConfirmDelete(null)}
        title="Delete this expense?"
        // Delete is for a line that should never have existed. Stopping a
        // charge is an edit that sets Effective to — say so, or people will delete
        // history that past invoices are explained by.
        description="This removes the line outright and cannot be undone. To stop billing it from a date onward, edit it and set “Effective to” instead."
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        confirmDisabled={deleteMutation.isPending}
        destructive
        onConfirm={() => {
          if (confirmDelete && !deleteMutation.isPending) deleteMutation.mutate(confirmDelete.id);
        }}
      />
    </div>
  );
}
